import { AnimatePresence, motion } from "framer-motion";
import {
    ChevronLeft,
    ChevronRight,
    LogOut,
    UserCircle2,
    X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";

import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { ROLES } from "../../lib/constants";
import { initials } from "../../lib/format";
import { effectiveRoles } from "../../lib/roles";
import { NAV_ITEMS } from "../../config/navItems";
import ConfirmModal from "../common/ConfirmModal";

function useSidebarItems(user) {
    const location = useLocation();
    const [vivierCount, setVivierCount] = useState(null);
    const [permanentsExpiringCount, setPermanentsExpiringCount] = useState(null);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        async function fetchCount() {
            try {
                const { data } = await api.get("/candidates/source-counts", { params: { pool: "only" } });
                if (!cancelled) setVivierCount(data?.total ?? 0);
            } catch (_e) {
                if (!cancelled) setVivierCount(null);
            }
        }
        fetchCount();
        const onChange = () => fetchCount();
        window.addEventListener("vivier:changed", onChange);
        return () => {
            cancelled = true;
            window.removeEventListener("vivier:changed", onChange);
        };
    }, [user, location.pathname]);

    // Permanents J-7 badge — surfaces "contracts due to renew" only for
    // roles that can act on them (admin + référent). Silently no-ops for
    // pre_recruteur (403 on the endpoint) so we don't spam network calls.
    useEffect(() => {
        if (!user) return;
        if (!effectiveRoles(user).has("admin")) return;
        let cancelled = false;
        async function fetchExpiring() {
            try {
                const { data } = await api.get("/permanents/expiring-soon/list", { params: { days: 7 } });
                if (!cancelled) setPermanentsExpiringCount(data?.total ?? 0);
            } catch (_e) {
                if (!cancelled) setPermanentsExpiringCount(null);
            }
        }
        fetchExpiring();
        const onChange = () => fetchExpiring();
        window.addEventListener("permanents:changed", onChange);
        return () => {
            cancelled = true;
            window.removeEventListener("permanents:changed", onChange);
        };
    }, [user, location.pathname]);

    if (!user) return [];
    const userRoles = effectiveRoles(user);
    // iter110m-hotfix6 · Ajout branche `coordinateur` — sinon le fallback
    // "referent" donnait le mauvais libellé (« Mes immersions » au lieu
    // de « Candidats ») dans la sidebar du Coord.
    const primaryForLabel = userRoles.has("admin") ? "admin"
        : userRoles.has("pre_recruteur") ? "pre_recruteur"
        : userRoles.has("coordinateur") ? "coordinateur"
        : "referent";
    // CEO 2026-09-30 · `hidden_nav` = onglets explicitement décochés par
    // l'admin pour cet utilisateur. On les retire APRÈS le filtre rôle
    // pour préserver la logique legacy pour tous les autres users.
    const hiddenNav = new Set(user.hidden_nav || []);
    return NAV_ITEMS
        .filter((i) => i.roles.some((r) => userRoles.has(r)) || (typeof i.extraAccess === "function" && i.extraAccess(user)))
        .filter((i) => !hiddenNav.has(i.to))
        .map((i) => {
            const out = { ...i, label: i.label_by_role?.[primaryForLabel] || i.label };
            if (i.to === "/vivier" && vivierCount !== null) out.badge = vivierCount;
            if (i.to === "/permanents" && permanentsExpiringCount !== null && permanentsExpiringCount > 0) {
                out.badge = permanentsExpiringCount;
            }
            return out;
        });
}

function BrandLogo({ onClick, collapsed = false }) {
    return (
        <Link
            to="/dashboard"
            onClick={onClick}
            data-testid="brand-logo"
            title={collapsed ? "PREREC by Shariti — Retour au cockpit" : "Retour au cockpit"}
            className={`flex items-center rounded-xl -mx-1 hover:bg-shariti-orange-soft/40 transition group ${collapsed ? "justify-center px-0 py-1" : "gap-2.5 px-1 py-1"}`}
        >
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-shariti-orange to-shariti-orange-light flex items-center justify-center shadow-btn group-hover:shadow-[0_6px_18px_rgba(238,114,46,0.4)] transition shrink-0">
                <span className="text-white font-black text-base">P</span>
            </div>
            {!collapsed && (
                <div>
                    <div className="font-extrabold tracking-tight text-shariti-black leading-none group-hover:text-shariti-orange transition">PREREC</div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mt-1">by Shariti</div>
                </div>
            )}
        </Link>
    );
}

function NavList({ items, onItemClick, collapsed = false }) {
    return (
        <nav className={`flex-1 min-h-0 overflow-y-auto py-3 space-y-0.5 ${collapsed ? "px-2" : "px-3"}`}>
            {items.map((item) => (
                <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onItemClick}
                    data-testid={`nav-${item.to.replace("/", "")}`}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) =>
                        `flex items-center gap-3 rounded-xl text-sm font-bold transition-all relative ${
                            collapsed ? "px-2 py-2.5 justify-center" : "px-3 py-2.5"
                        } ${
                            isActive
                                ? "bg-shariti-orange-soft text-shariti-orange"
                                : "text-slate-600 hover:bg-shariti-gray hover:text-shariti-orange"
                        }`
                    }
                >
                    {({ isActive }) => (
                        <>
                            <item.icon className="h-4.5 w-4.5 shrink-0" strokeWidth={isActive ? 2.6 : 2.2} />
                            {!collapsed && (
                                <span className="flex-1 leading-tight break-words">{item.label}</span>
                            )}
                            {!collapsed && typeof item.badge === "number" && item.badge > 0 && (
                                <span
                                    data-testid={`nav-badge-${item.to.replace("/", "")}`}
                                    className={`min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center text-[10px] font-extrabold rounded-full ${
                                        isActive
                                            ? "bg-shariti-orange text-white"
                                            : "bg-amber-100 text-amber-800 border border-amber-200"
                                    }`}
                                >
                                    {item.badge > 99 ? "99+" : item.badge}
                                </span>
                            )}
                            {collapsed && typeof item.badge === "number" && item.badge > 0 && (
                                <span
                                    aria-hidden
                                    className="absolute top-1 right-1 h-2 w-2 rounded-full bg-shariti-orange ring-2 ring-white"
                                />
                            )}
                            {!collapsed && isActive && !item.badge && (
                                <motion.span
                                    layoutId="nav-pill"
                                    className="ml-auto h-1.5 w-1.5 rounded-full bg-shariti-orange"
                                />
                            )}
                        </>
                    )}
                </NavLink>
            ))}
        </nav>
    );
}

function UserMenu({ user, userRoles, onLogout, onNavigateProfile, collapsed = false }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        }
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [menuOpen]);

    return (
        <div className={`border-t border-shariti-gray py-3 relative shrink-0 ${collapsed ? "px-2" : "px-3"}`} ref={menuRef}>
            {menuOpen && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`absolute bottom-full mb-2 bg-white rounded-2xl shadow-2xl border border-shariti-gray p-1.5 z-50 ${collapsed ? "left-14 w-52" : "left-3 right-3"}`}
                    data-testid="sidebar-user-menu"
                >
                    <button
                        onClick={() => { setMenuOpen(false); onNavigateProfile(); }}
                        data-testid="sidebar-menu-profile"
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-bold text-slate-600 hover:bg-shariti-orange-soft hover:text-shariti-orange transition"
                    >
                        <UserCircle2 className="h-4 w-4" />
                        Mon profil
                    </button>
                    <button
                        onClick={() => { setMenuOpen(false); onLogout(); }}
                        data-testid="sidebar-menu-logout"
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-bold text-slate-600 hover:bg-red-50 hover:text-shariti-red transition"
                    >
                        <LogOut className="h-4 w-4" />
                        Se déconnecter
                    </button>
                </motion.div>
            )}
            <button
                onClick={() => setMenuOpen((m) => !m)}
                data-testid="logout-button"
                title={collapsed ? `${user.name} — Mon compte` : "Mon compte"}
                className={`group w-full flex items-center rounded-2xl hover:bg-shariti-orange-soft transition border-2 border-transparent hover:border-shariti-orange/20 ${collapsed ? "justify-center px-1 py-1.5" : "gap-3 px-3 py-2.5"}`}
            >
                {user.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover shrink-0" />
                ) : (
                    <div className="h-9 w-9 rounded-full bg-shariti-orange-soft text-shariti-orange font-extrabold flex items-center justify-center shrink-0">
                        {initials(user.name)}
                    </div>
                )}
                {!collapsed && (
                    <div className="min-w-0 flex-1 text-left">
                        <div className="text-sm font-bold text-shariti-black break-words group-hover:text-shariti-orange transition">
                            {user.name}
                        </div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 group-hover:text-shariti-orange transition break-words">
                            {Array.from(userRoles).map((r) => ROLES[r]?.short || r).join(" + ")}
                        </div>
                    </div>
                )}
                {!collapsed && (
                    <div className="h-8 w-8 rounded-xl bg-shariti-cream text-slate-500 group-hover:bg-shariti-orange group-hover:text-white transition flex items-center justify-center shrink-0">
                        <UserCircle2 className="h-4 w-4" />
                    </div>
                )}
            </button>
        </div>
    );
}

export default function Sidebar({ mobileOpen = false, onMobileClose }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [logoutOpen, setLogoutOpen] = useState(false);
    // Persistent desktop collapse — the CEO wanted the ability to reclaim
    // horizontal space on data-dense pages (Embauche board, Calendar).
    // Rehydrated from localStorage so the choice sticks across sessions.
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem("prerec_sidebar_collapsed") === "1";
        } catch {
            return false;
        }
    });
    const toggleCollapsed = () => {
        setCollapsed((c) => {
            const next = !c;
            try {
                localStorage.setItem("prerec_sidebar_collapsed", next ? "1" : "0");
            } catch {
                // ignore quota / privacy-mode errors
            }
            return next;
        });
    };
    const items = useSidebarItems(user);

    // Close mobile drawer automatically on route change
    useEffect(() => {
        if (mobileOpen && onMobileClose) onMobileClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Lock body scroll while drawer is open
    useEffect(() => {
        if (mobileOpen) {
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = ""; };
        }
    }, [mobileOpen]);

    if (!user) return null;
    const userRoles = effectiveRoles(user);

    const userMenu = (
        <UserMenu
            user={user}
            userRoles={userRoles}
            onLogout={() => setLogoutOpen(true)}
            onNavigateProfile={() => navigate("/mon-profil")}
            collapsed={collapsed}
        />
    );

    return (
        <>
            {/* === DESKTOP SIDEBAR === */}
            <aside
                className={`hidden lg:flex shrink-0 flex-col bg-white border-r border-shariti-gray h-screen sticky top-0 z-30 transition-[width] duration-200 ease-out ${collapsed ? "w-16" : "w-64"}`}
                data-testid="sidebar-desktop"
                data-collapsed={collapsed ? "1" : "0"}
            >
                <div className={`pt-5 pb-3 shrink-0 ${collapsed ? "px-3" : "px-6"} relative`}>
                    <BrandLogo collapsed={collapsed} />
                    <button
                        type="button"
                        onClick={toggleCollapsed}
                        data-testid="sidebar-collapse-toggle"
                        title={collapsed ? "Déployer le menu" : "Réduire le menu"}
                        aria-label={collapsed ? "Déployer le menu" : "Réduire le menu"}
                        className="absolute -right-3 top-9 h-6 w-6 inline-flex items-center justify-center rounded-full bg-white border border-shariti-gray text-slate-500 hover:text-shariti-orange hover:border-shariti-orange shadow-sm hover:shadow transition z-40"
                    >
                        {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
                    </button>
                </div>
                <NavList items={items} collapsed={collapsed} />
                {userMenu}
            </aside>

            {/* === MOBILE DRAWER === */}
            <AnimatePresence>
                {mobileOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={onMobileClose}
                            data-testid="mobile-sidebar-overlay"
                            className="fixed inset-0 z-40 bg-black/45 backdrop-blur-[2px] lg:hidden"
                        />
                        <motion.aside
                            initial={{ x: "-100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "-100%" }}
                            transition={{ type: "tween", duration: 0.22, ease: "easeOut" }}
                            data-testid="mobile-sidebar"
                            className="fixed top-0 left-0 z-50 h-[100dvh] w-[84vw] max-w-[320px] flex flex-col bg-white border-r border-shariti-gray shadow-2xl lg:hidden"
                        >
                            <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
                                <BrandLogo onClick={onMobileClose} />
                                <button
                                    onClick={onMobileClose}
                                    data-testid="mobile-sidebar-close"
                                    aria-label="Fermer le menu"
                                    className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-cream border border-shariti-gray text-slate-500 hover:text-shariti-orange transition"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <NavList items={items} onItemClick={onMobileClose} />
                            {userMenu}
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>

            {logoutOpen && (
                <ConfirmModal
                    title="Se déconnecter ?"
                    body={(
                        <>
                            Vous serez redirigé(e) vers l'écran de connexion. Toute action en cours non enregistrée sera perdue.
                        </>
                    )}
                    confirmLabel="Se déconnecter"
                    cancelLabel="Rester connecté"
                    tone="red"
                    icon={LogOut}
                    onCancel={() => setLogoutOpen(false)}
                    onConfirm={() => {
                        setLogoutOpen(false);
                        logout();
                        navigate("/login");
                    }}
                    testid="logout-confirm-modal"
                />
            )}
        </>
    );
}
